import {
	Card,
	CardContent,
	CircularProgress,
	Grid2,
	Paper,
	Typography
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import { useFormik } from "formik";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import { useNavigationInterceptorContext } from "../../../contexts/useNavigationInterceptorContext";
import {
	usePatchMyAvailabilityFreelancerOnboardingStep,
	useSaveMyAvailabilityFreelancerOnboardingProgress
} from "../../../hooks/useFreelancerProfile";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";
import { FreelancerProfile } from "../../../models/FreelancerProfile";
import { zoullooColors } from "../../../theme";
import {
	AvailabilityViewModel,
	FreelancerOnboardingMyAvailabilityStepViewModel,
	TimeCommitmentViewModel
} from "../../../ViewModels/FreelancerOnboardingMyAvailabilityStepViewModel";
import AvailabilitySelector from "./components/availability/AvailabilitySelector";
import DaysPerWeekInput from "./components/availability/DaysPerWeekInput";
import InvalidMissionEndDateDialog from "./components/availability/InvalidMissionEndDateDialog";
import MissionEndDatePicker from "./components/availability/MissionEndDatePicker";
import MissionTimeCommitmentSelector from "./components/availability/MissionTimeCommitmentSelector";
import NavigationButtons from "./components/availability/NavigationButtons";
import TimeCommitmentSelector from "./components/availability/TimeCommitmentSelector";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";

export default function FreelancerOnboardingMyAvailability() {
	const { t } = useTranslation();
	const {
		setFreelancerOnboardingMyAvailabilityStepViewModel,
		setCurrentOnboardingFormStep,
		setNextOnboardingFormStep: setNextOnboardingStep,
		freelancerProfileQueryResult
	} = useFreelancerContext();
	const { data: freelancerProfile, isLoading: isFreelancerProfileLoading } =
		freelancerProfileQueryResult;
	const patchMyAvailabilityFreelancerOnboardingStep =
		usePatchMyAvailabilityFreelancerOnboardingStep();
	const saveMyAvailabilityFreelancerOnboardingProgress =
		useSaveMyAvailabilityFreelancerOnboardingProgress({ hideAllSnackbars: true });
	const { interceptNavigation } = useNavigationInterceptorContext();

	const validationSchema = Yup.object({
		selectedAvailability: Yup.string().required(
			t("freelancerOnboardingMyAvailability.validation.availabilityRequired")
		),
		selectedTimeCommitment: Yup.string().when(
			["selectedAvailability", "missionEndDate"],
			{
				is: (
					selectedAvailability: AvailabilityViewModel,
					missionEndDate: Dayjs | null
				) => {
					return (
						selectedAvailability === "available" ||
						(selectedAvailability === "onMission" &&
							missionEndDate !== null &&
							dayjs(missionEndDate).isAfter(dayjs().subtract(1, "day")))
					);
				},
				then: schema =>
					schema.required(
						t("freelancerOnboardingMyAvailability.validation.timeCommitmentRequired")
					),
				otherwise: schema => schema.nullable()
			}
		),
		daysPerWeek: Yup.number()
			.min(1, t("freelancerOnboardingMyAvailability.validation.daysPerWeekMin"))
			.max(7, t("freelancerOnboardingMyAvailability.validation.daysPerWeekMax"))
			.when(["selectedAvailability", "selectedTimeCommitment", "missionEndDate"], {
				is: (
					selectedAvailability: AvailabilityViewModel,
					selectedTimeCommitment: TimeCommitmentViewModel,
					missionEndDate: Dayjs | null
				) => {
					return (
						(selectedAvailability === "available" &&
							selectedTimeCommitment === "partTime") ||
						(selectedAvailability === "onMission" &&
							selectedTimeCommitment === "partTime" &&
							missionEndDate !== null &&
							dayjs(missionEndDate).isAfter(dayjs().subtract(1, "day")))
					);
				},
				then: schema =>
					schema.required(
						t("freelancerOnboardingMyAvailability.validation.daysPerWeekRequired")
					),
				otherwise: schema => schema.nullable()
			}),
		missionEndDate: Yup.mixed().when("selectedAvailability", {
			is: "onMission",
			then: schema =>
				schema
					.required(
						t("freelancerOnboardingMyAvailability.validation.missionEndDateRequired")
					)
					.test(
						"is-future-date",
						t("freelancerOnboardingMyAvailability.validation.missionEndDateFuture"),
						value => {
							if (!value) return true;
							return dayjs(value as Dayjs).isAfter(dayjs().subtract(1, "day"));
						}
					),
			otherwise: schema => schema.nullable()
		})
	});

	const isFreelancerProfileAvailable = (
		freelancerProfile: FreelancerProfile
	): AvailabilityViewModel => {
		return freelancerProfile.availability?.isAvailable === true
			? "available"
			: "onMission";
	};

	const isFreelancerProfileWouldWorkFullTime = (
		freelancerProfile: FreelancerProfile
	): TimeCommitmentViewModel => {
		return freelancerProfile.availability?.isFullTime === true ? "fullTime" : "partTime";
	};

	const initialValues: FreelancerOnboardingMyAvailabilityStepViewModel = {
		selectedAvailability:
			freelancerProfile?.availability?.isAvailable === undefined ||
			freelancerProfile?.availability.isAvailable === null
				? null
				: isFreelancerProfileAvailable(freelancerProfile),
		selectedTimeCommitment:
			freelancerProfile?.availability?.isFullTime === undefined ||
			freelancerProfile?.availability.isFullTime === null
				? null
				: isFreelancerProfileWouldWorkFullTime(freelancerProfile),
		missionEndDate: freelancerProfile?.availability?.missionEndDate
			? dayjs(freelancerProfile.availability.missionEndDate)
			: null,
		daysPerWeek: freelancerProfile?.availability?.daysPerWeek ?? null,
		form: null
	};

	const formik = useFormik({
		initialValues,
		validationSchema,
		validateOnChange: true,
		validateOnBlur: true,
		enableReinitialize: true,
		onSubmit: async values => {
			try {
				await patchMyAvailabilityFreelancerOnboardingStep.mutateAsync({
					freelancerOnboardingMyAvailabilityStepViewModel: values,
					freelancerProfile: freelancerProfile!,
					newStep:
						freelancerProfile?.onboarding.currentStatus ===
						FreelancerOnboardingStatus.Accepted
							? FreelancerOnboardingFormStep.MyAvailability
							: FreelancerOnboardingFormStep.MyProfessionalInformation
				});

				if (
					freelancerProfile?.onboarding.currentStatus !==
					FreelancerOnboardingStatus.Accepted
				) {
					setCurrentOnboardingFormStep(
						FreelancerOnboardingFormStep.MyProfessionalInformation
					);
				}
			} catch (error) {
				console.error("Error submitting form:", error);
				formik.setFieldValue("form.isSubmitting", false);
			}
		}
	});

	useEffect(() => {
		setFreelancerOnboardingMyAvailabilityStepViewModel({
			...formik.values,
			form: {
				isValid: validationSchema.isValidSync(formik.values),
				isSubmitting: formik.isSubmitting,
				isDirty: formik.dirty
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values]);

	const isMissionEndDateValid = () => {
		const { missionEndDate } = formik.values;
		if (!missionEndDate) return false;
		return dayjs(missionEndDate).isAfter(dayjs().subtract(1, "day"));
	};

	const handleAvailabilitySelection = (availability: AvailabilityViewModel) => {
		const newValue =
			formik.values.selectedAvailability === availability ? null : availability;

		formik.setFieldValue("selectedAvailability", newValue);
		formik.setFieldTouched("selectedAvailability", true);

		// Reset time commitment when changing availability
		formik.setFieldValue("selectedTimeCommitment", null);
		formik.setFieldTouched("selectedTimeCommitment", false);

		// Reset mission end date when changing availability
		if (newValue !== "onMission") {
			formik.setFieldValue("missionEndDate", null);
			formik.setFieldTouched("missionEndDate", false);
		}

		formik.setFieldValue("daysPerWeek", null);

		// Use a very short timeout to allow state to update before validation
		setTimeout(() => {
			formik.validateField("selectedAvailability");
			formik.validateField("selectedTimeCommitment");
		}, 1);
	};

	const handleTimeCommitmentSelection = (timeCommitment: TimeCommitmentViewModel) => {
		const newValue =
			formik.values.selectedTimeCommitment === timeCommitment ? null : timeCommitment;

		formik.setFieldValue("selectedTimeCommitment", newValue);
		formik.setFieldTouched("selectedTimeCommitment", true);

		// Use a very short timeout to allow state to update before validation
		setTimeout(() => {
			formik.validateField("selectedTimeCommitment");
		}, 1);

		if (newValue === "fullTime") {
			formik.setFieldValue("daysPerWeek", null);
			return;
		} else if (newValue === "partTime") {
			formik.setFieldValue("daysPerWeek", 1);
			return;
		}

		formik.setFieldValue("daysPerWeek", null);
	};

	if (isFreelancerProfileLoading) {
		return <CircularProgress />;
	}

	return (
		<form onSubmit={formik.handleSubmit}>
			<Card
				component={Paper}
				elevation={0}
				sx={{
					margin: "32px"
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<Typography
						variant="h2"
						component="h1"
						gutterBottom
						sx={{ fontWeight: "bold", color: zoullooColors.primary }}
					>
						{t("freelancerOnboardingMyAvailability.title")}
					</Typography>
					<Typography
						variant="body1"
						color="textSecondary"
						sx={{ mb: 3, whiteSpace: "pre-line" }}
					>
						{t("freelancerOnboardingMyAvailability.description")}
					</Typography>

					<AvailabilitySelector
						selectedAvailability={formik.values.selectedAvailability}
						onAvailabilitySelect={handleAvailabilitySelection}
						touched={formik.touched.selectedAvailability || false}
						error={formik.errors.selectedAvailability}
					/>

					{/* Mission End Date - Only show when "onMission" is selected */}

					<Grid2
						container
						spacing={2}
						sx={{
							mb: 0,
							visibility: formik.values.selectedAvailability ? "visible" : "collapse"
						}}
					>
						{/* Time Commitment Selection - Only show when "available" is selected */}

						<TimeCommitmentSelector
							selectedTimeCommitment={formik.values.selectedTimeCommitment}
							selectedAvailability={formik.values.selectedAvailability}
							onTimeCommitmentSelect={handleTimeCommitmentSelection}
							daysPerWeek={formik.values.daysPerWeek}
							onDaysPerWeekChange={days => {
								formik.setFieldValue("daysPerWeek", days);
								formik.setFieldTouched("daysPerWeek", true);
							}}
							touched={formik.touched.selectedTimeCommitment || false}
							error={formik.errors.selectedTimeCommitment}
							daysPerWeekTouched={formik.touched.daysPerWeek || false}
							daysPerWeekError={formik.errors.daysPerWeek}
							showDaysPerWeek={
								formik.values.selectedAvailability === "available" &&
								formik.values.selectedTimeCommitment === "partTime"
							}
						/>

						{/* Mission End Date - Only show when "onMission" is selected */}

						<MissionEndDatePicker
							value={formik.values.missionEndDate}
							selectedAvailability={formik.values.selectedAvailability}
							onChange={newValue => {
								formik.setFieldValue("missionEndDate", newValue);
								formik.setFieldTouched("missionEndDate", true);

								// Reset time commitment when date changes or is invalid
								const dateIsValid =
									newValue && dayjs(newValue).isAfter(dayjs().subtract(1, "day"));
								if (!dateIsValid) {
									formik.setFieldValue("selectedTimeCommitment", null);
									formik.setFieldTouched("selectedTimeCommitment", false);
								}

								// Use a very short timeout to allow state to update before validation
								setTimeout(() => {
									formik.validateField("missionEndDate");
									formik.validateField("selectedTimeCommitment");
								}, 1);
							}}
							onBlur={() => {
								formik.setFieldTouched("missionEndDate", true);
							}}
							touched={formik.touched.missionEndDate || false}
							error={formik.errors.missionEndDate}
							isValid={isMissionEndDateValid()}
						/>
					</Grid2>

					<Grid2
						container
						spacing={2}
						sx={{
							mt: 3,
							mb: 4,
							visibility:
								formik.values.selectedAvailability === "onMission" &&
								formik.values.missionEndDate &&
								isMissionEndDateValid()
									? "visible"
									: "collapse"
						}}
					>
						<MissionTimeCommitmentSelector
							selectedTimeCommitment={formik.values.selectedTimeCommitment}
							selectedAvailability={formik.values.selectedAvailability}
							onTimeCommitmentSelect={handleTimeCommitmentSelection}
							touched={formik.touched.selectedTimeCommitment || false}
							error={formik.errors.selectedTimeCommitment}
						/>

						<DaysPerWeekInput
							value={formik.values.daysPerWeek}
							onChange={days => {
								formik.setFieldValue("daysPerWeek", days);
								formik.setFieldTouched("daysPerWeek", true);
							}}
							touched={formik.touched.daysPerWeek || false}
							error={formik.errors.daysPerWeek}
							inline={true}
							visible={
								formik.values.selectedAvailability === "onMission" &&
								formik.values.selectedTimeCommitment === "partTime" &&
								isMissionEndDateValid()
							}
						/>
					</Grid2>

					{/* Navigation Buttons */}
					<NavigationButtons
						freelancerProfile={freelancerProfile!}
						onReturnToPreviousStep={async () => {
							interceptNavigation({
								onValidNavigation: async () => {
									try {
										await saveMyAvailabilityFreelancerOnboardingProgress.mutateAsync({
											freelancerOnboardingMyAvailabilityStepViewModel: formik.values,
											freelancerProfile: freelancerProfile!,
											newStep: FreelancerOnboardingFormStep.MyPresentation
										});

										setCurrentOnboardingFormStep(
											FreelancerOnboardingFormStep.MyPresentation
										);
									} catch (error) {
										console.error("Error returning back to the previous step:", error);
									}
								},
								onBlockedNavigation: () => {
									setNextOnboardingStep(FreelancerOnboardingFormStep.MyPresentation);
								}
							});
						}}
						isFormValid={validationSchema.isValidSync(formik.values)}
						isSubmitting={formik.isSubmitting}
					/>
				</CardContent>
			</Card>

			<InvalidMissionEndDateDialog />
		</form>
	);
}
